<template>
    <div>
        <a
            class="flex items-center w-full px-4 py-2 bg-white border border-gray-300 rounded shadow-sm cursor-pointer | hover:bg-gray-100 hover:border-gray-400"
            target="_blank"
            :href="effectiveUrl || undefined"
            @mouseenter="prefetchUrl"
            @focus="prefetchUrl"
            @click="onClick"
        >
            <div class="w-full flex items-center">
                <div class="mr-3">
                    <icon class="text-3xl" :name="icon" />
                </div>
                <div class="text-left overflow-hidden">
                    <div class="truncate" v-text="attachment.name" />
                    <div class="text-sm text-grey-600">{{ sizeInMb }} MB</div>
                </div>
            </div>
        </a>
    </div>
</template>

<script>
    import { round } from '@/utils/numbers.js';
    import { isSignedUrlExpired } from '@/plugins/signedUrl/cache.ts';

    export default {
        props: {
            attachment: {
                type: Object,
                require: true,
            },
            communicationId: {
                type: [Number, String],
                default: null,
            },
            attachmentIndex: {
                type: Number,
                default: null,
            },
        },

        data() {
            return {
                resolvedUrl: null,
                resolvingUrl: false,
                resolvingUrlPromise: null,
                isMounted: false,
            };
        },

        computed: {
            // Prefer a freshly resolved signed URL when we have one. Fall back
            // to whatever the payload originally carried so the legacy flow
            // (no communicationId / no resolver) keeps working.
            effectiveUrl() {
                if (this.resolvedUrl && !isSignedUrlExpired(this.resolvedUrl)) {
                    return this.resolvedUrl;
                }

                if (this.canResolve) {
                    return null;
                }

                return this.attachment.url || this.attachment.path || null;
            },

            canResolve() {
                return this.communicationId !== null && this.attachmentIndex !== null;
            },

            needsResolve() {
                if (!this.canResolve) {
                    return false;
                }

                return !this.resolvedUrl || isSignedUrlExpired(this.resolvedUrl);
            },

            icon() {
                if (!this.attachment.contentType) {
                    return 'regular/common-file-empty';
                }

                if (this.attachment.contentType.includes('video/')) {
                    return 'regular/video-file-play';
                }

                if (this.attachment.contentType.includes('image/')) {
                    return 'regular/image-file-camera';
                }

                if (this.attachment.contentType.includes('audio/')) {
                    return 'regular/audio-file';
                }

                if (this.attachment.contentType.includes('text/')) {
                    return 'regular/common-file-text';
                }

                if (this.attachment.contentType.includes('excel')) {
                    return 'regular/office-file-xls';
                }

                if (
                    this.attachment.contentType.includes('msword') ||
                    this.attachment.contentType.includes('ms-word')
                ) {
                    return 'regular/office-file-doc';
                }

                if (this.attachment.contentType.includes('powerpoint')) {
                    return 'regular/office-file-pdf';
                }

                if (this.attachment.contentType.includes('pdf')) {
                    return 'regular/office-file-pdf';
                }

                return 'regular/common-file-empty';
            },

            sizeInMb() {
                return round(this.attachment.size / 1000000, 2);
            },
        },

        methods: {
            async resolveUrl() {
                if (!this.canResolve) {
                    return null;
                }

                if (this.resolvedUrl && !isSignedUrlExpired(this.resolvedUrl)) {
                    return this.resolvedUrl;
                }

                if (this.resolvingUrlPromise) {
                    return this.resolvingUrlPromise;
                }

                this.resolvingUrl = true;

                this.resolvingUrlPromise = this.$signedUrl
                    .resolve('communicationAttachment', this.communicationId, this.attachmentIndex)
                    .catch(() => null);

                let resolved = null;
                try {
                    resolved = await this.resolvingUrlPromise;
                } catch (_) {
                    resolved = null;
                } finally {
                    // Guard the assignment so a slow resolve that lands
                    // after the component was unmounted doesn't try to
                    // mutate stale reactive state (Vue 3 strict mode
                    // warns on it).
                    if (this.isMounted) {
                        this.resolvedUrl = resolved;
                        this.resolvingUrl = false;
                        this.resolvingUrlPromise = null;
                    }
                }

                return resolved;
            },

            // Prefetch on hover/focus so the anchor already carries a fresh
            // href by the time the user clicks — avoids the popup-blocker /
            // about:blank glitch when we have to wait on the network during
            // the click handler.
            async prefetchUrl() {
                if (!this.needsResolve || this.resolvingUrl) {
                    return;
                }

                await this.resolveUrl();
            },

            async onClick(event) {
                if (!this.canResolve) {
                    return;
                }

                if (this.resolvedUrl && !isSignedUrlExpired(this.resolvedUrl)) {
                    return;
                }

                event.preventDefault();

                const newTab = window.open('', '_blank');
                if (newTab) {
                    newTab.opener = null;
                }

                const url = await this.resolveUrl();
                const fallbackUrl = this.attachment.url || this.attachment.path || null;
                const target = url || fallbackUrl;

                if (newTab && target) {
                    newTab.location.href = target;
                } else if (newTab) {
                    newTab.close();
                } else if (target) {
                    window.open(target, '_blank', 'noopener');
                }
            },
        },

        mounted() {
            this.isMounted = true;
            // Prefetch eagerly so the anchor's href is real on first
            // hover/click. Without this the link briefly carries `#`
            // and the browser shows the no-drop cursor on hover.
            if (this.canResolve) {
                this.resolveUrl();
            }
        },

        beforeUnmount() {
            this.isMounted = false;
        },
    };
</script>
